import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import BlockRoundedIcon from "@mui/icons-material/BlockRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SaveRoundedIcon from "@mui/icons-material/SaveRounded";
import { useParams } from "react-router-dom";
import { appointmentsApi } from "../../api/appointments";
import { productsApi } from "../../api/products";
import ModuleDisabledState from "../../components/dashboard/ModuleDisabledState";
import { useProjects } from "../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../utils/projectModules";

const BRAND = { orange: "#F35A3A", dark: "#19242F" };
const TIMEZONE = "America/Argentina/Mendoza";
const ACTIVE_STATUSES = ["pending", "confirmed"];

const DAYS = [
  { weekday: 1, label: "Lunes" },
  { weekday: 2, label: "Martes" },
  { weekday: 3, label: "Miércoles" },
  { weekday: 4, label: "Jueves" },
  { weekday: 5, label: "Viernes" },
  { weekday: 6, label: "Sábado" },
  { weekday: 0, label: "Domingo" },
];

const emptyAppointment = {
  fullName: "",
  phone: "",
  email: "",
  startAt: "",
  notes: "",
  status: "confirmed",
  paymentStatus: "not_required",
};

const emptyBlock = {
  scope: "service",
  startAt: "",
  endAt: "",
  reason: "",
};

function normalizeType(item = {}) {
  const value = String(item.itemType || item.type || item.tipo || "")
    .trim()
    .toLowerCase();
  return ["service", "servicio"].includes(value) ? "service" : "product";
}

function itemId(item = {}) {
  return item.id || item._id || "";
}

function itemName(item = {}) {
  return item.name || item.nombre || "Servicio";
}

function requiresBooking(item = {}) {
  return Boolean(item.requiresBooking ?? item.requiereReserva);
}

function durationLabel(item = {}) {
  const minutes = Number(item.durationMinutes ?? item.duracionMinutos ?? 0);
  if (!minutes) return "Sin duración";
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rest = minutes % 60;
  return rest ? `${hours} h ${rest} min` : `${hours} h`;
}

function localInputToIso(value) {
  if (!value) return "";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "" : date.toISOString();
}

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("es-AR", {
    dateStyle: "short",
    timeStyle: "short",
  }).format(date);
}

function statusLabel(status) {
  return {
    pending: "Pendiente",
    confirmed: "Confirmado",
    cancelled: "Cancelado",
    completed: "Completado",
    no_show: "No asistió",
  }[status] || status;
}

function paymentLabel(status) {
  return {
    not_required: "No requerido",
    pending: "Pendiente",
    in_process: "En proceso",
    approved: "Pagado",
    rejected: "Rechazado",
    cancelled: "Cancelado",
    refunded: "Devuelto",
  }[status] || status;
}

function defaultSchedule() {
  return DAYS.map((day) => ({
    ...day,
    enabled: day.weekday >= 1 && day.weekday <= 5,
    startTime: "09:00",
    endTime: "18:00",
  }));
}

function rulesToSchedule(rules = []) {
  const firstByDay = new Map();
  for (const rule of rules) {
    if (rule?.active === false || firstByDay.has(Number(rule.weekday))) continue;
    firstByDay.set(Number(rule.weekday), rule);
  }
  return DAYS.map((day) => {
    const rule = firstByDay.get(day.weekday);
    return {
      ...day,
      enabled: Boolean(rule),
      startTime: rule?.startTime || "09:00",
      endTime: rule?.endTime || "18:00",
    };
  });
}

function rangeForAgenda() {
  const from = new Date();
  from.setHours(0, 0, 0, 0);
  const to = new Date(from);
  to.setDate(to.getDate() + 60);
  to.setHours(23, 59, 59, 999);
  return { from: from.toISOString(), to: to.toISOString() };
}

export default function ProjectAgendaPage() {
  const { projectId } = useParams();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [services, setServices] = React.useState([]);
  const [selectedServiceId, setSelectedServiceId] = React.useState("");
  const [appointments, setAppointments] = React.useState([]);
  const [blocks, setBlocks] = React.useState([]);
  const [schedule, setSchedule] = React.useState(defaultSchedule);
  const [loading, setLoading] = React.useState(false);
  const [savingSchedule, setSavingSchedule] = React.useState(false);
  const [savingAppointment, setSavingAppointment] = React.useState(false);
  const [savingBlock, setSavingBlock] = React.useState(false);
  const [error, setError] = React.useState("");
  const [success, setSuccess] = React.useState("");
  const [appointmentDialog, setAppointmentDialog] = React.useState(false);
  const [blockDialog, setBlockDialog] = React.useState(false);
  const [appointmentForm, setAppointmentForm] = React.useState(emptyAppointment);
  const [blockForm, setBlockForm] = React.useState(emptyBlock);

  const project = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const productsEnabled = Boolean(
    project && isProjectModuleEnabled(project, "products"),
  );
  const selectedService = React.useMemo(
    () => services.find((service) => itemId(service) === selectedServiceId),
    [services, selectedServiceId],
  );
  const projectTimezone = project?.timezone || TIMEZONE;

  React.useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  React.useEffect(() => {
    setServices([]);
    setSelectedServiceId("");
    setAppointments([]);
    setBlocks([]);
    setSchedule(defaultSchedule());
    setError("");
    setSuccess("");
  }, [projectId]);

  React.useEffect(() => {
    if (projectId && (!activeProject || activeProject.slug !== projectId)) {
      const found = projects.find(
        (item) => item.slug === projectId || item.id === projectId,
      );
      if (found) setActiveProject(found);
    }
  }, [projectId, projects, activeProject, setActiveProject]);

  const loadServices = React.useCallback(async () => {
    if (!projectId || !productsEnabled) return;
    const data = await productsApi.list(projectId, { type: "service" });
    const nextServices = (data.products || []).filter(
      (item) => normalizeType(item) === "service",
    );
    setServices(nextServices);
    setSelectedServiceId((current) => {
      if (nextServices.some((item) => itemId(item) === current)) return current;
      const preferred = nextServices.find(requiresBooking) || nextServices[0];
      return preferred ? itemId(preferred) : "";
    });
  }, [projectId, productsEnabled]);

  const loadAgenda = React.useCallback(async () => {
    if (!projectId || !selectedServiceId || !productsEnabled) {
      setAppointments([]);
      setBlocks([]);
      setSchedule(defaultSchedule());
      return;
    }
    try {
      setLoading(true);
      setError("");
      const range = rangeForAgenda();
      const [appointmentsData, availabilityData, blocksData] = await Promise.all([
        appointmentsApi.list(projectId, {
          ...range,
          serviceId: selectedServiceId,
        }),
        appointmentsApi.getAvailability(projectId, selectedServiceId),
        appointmentsApi.listBlocks(projectId, {
          ...range,
          serviceId: selectedServiceId,
        }),
      ]);
      setAppointments(appointmentsData.appointments || []);
      setSchedule(rulesToSchedule(availabilityData.rules || []));
      setBlocks(blocksData.blocks || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar la agenda");
    } finally {
      setLoading(false);
    }
  }, [projectId, selectedServiceId, productsEnabled]);

  React.useEffect(() => {
    if (!projectId || !productsEnabled) return;
    loadServices().catch((err) => {
      setError(err.message || "No se pudieron cargar los servicios");
    });
  }, [loadServices, projectId, productsEnabled]);

  React.useEffect(() => {
    loadAgenda();
  }, [loadAgenda]);

  const updateScheduleDay = (weekday, patch) => {
    setSchedule((current) =>
      current.map((day) =>
        day.weekday === weekday ? { ...day, ...patch } : day,
      ),
    );
  };

  const saveSchedule = async () => {
    if (!selectedServiceId) return;
    try {
      setSavingSchedule(true);
      setError("");
      setSuccess("");
      const rules = schedule
        .filter((day) => day.enabled)
        .map((day) => ({
          weekday: day.weekday,
          startTime: day.startTime,
          endTime: day.endTime,
          active: true,
        }));
      await appointmentsApi.saveAvailability(projectId, selectedServiceId, {
        timezone: projectTimezone,
        rules,
      });
      setSuccess("Disponibilidad semanal guardada.");
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo guardar la disponibilidad");
    } finally {
      setSavingSchedule(false);
    }
  };

  const createAppointment = async (event) => {
    event.preventDefault();
    try {
      setSavingAppointment(true);
      setError("");
      setSuccess("");
      await appointmentsApi.create(projectId, {
        serviceId: selectedServiceId,
        fullName: appointmentForm.fullName,
        phone: appointmentForm.phone,
        email: appointmentForm.email,
        startAt: localInputToIso(appointmentForm.startAt),
        notes: appointmentForm.notes,
        status: appointmentForm.status,
        paymentStatus: appointmentForm.paymentStatus,
        timezone: projectTimezone,
      });
      setAppointmentDialog(false);
      setAppointmentForm(emptyAppointment);
      setSuccess("Turno cargado en la agenda.");
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo crear el turno");
    } finally {
      setSavingAppointment(false);
    }
  };

  const createBlock = async (event) => {
    event.preventDefault();
    try {
      setSavingBlock(true);
      setError("");
      setSuccess("");
      await appointmentsApi.createBlock(projectId, {
        serviceId:
          blockForm.scope === "service" ? selectedServiceId : undefined,
        startAt: localInputToIso(blockForm.startAt),
        endAt: localInputToIso(blockForm.endAt),
        reason: blockForm.reason,
      });
      setBlockDialog(false);
      setBlockForm(emptyBlock);
      setSuccess("Horario bloqueado.");
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo bloquear el horario");
    } finally {
      setSavingBlock(false);
    }
  };

  const updateAppointment = async (appointmentId, payload) => {
    try {
      setError("");
      await appointmentsApi.update(projectId, appointmentId, payload);
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo actualizar el turno");
    }
  };

  const removeBlock = async (block) => {
    const ok = window.confirm("¿Quitar este bloqueo de la agenda?");
    if (!ok) return;
    try {
      await appointmentsApi.removeBlock(projectId, block.id || block._id);
      await loadAgenda();
    } catch (err) {
      setError(err.message || "No se pudo quitar el bloqueo");
    }
  };

  const summary = React.useMemo(() => {
    const now = new Date();
    return {
      upcoming: appointments.filter(
        (item) =>
          ACTIVE_STATUSES.includes(item.status) && new Date(item.startAt) >= now,
      ).length,
      pending: appointments.filter((item) => item.status === "pending").length,
      paid: appointments.filter((item) => item.payment?.status === "approved")
        .length,
      blocks: blocks.length,
    };
  }, [appointments, blocks]);

  if (project && !productsEnabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Agenda de servicios"
        description="La agenda depende del módulo de productos y servicios del proyecto."
      />
    );
  }

  return (
    <Stack spacing={3} sx={{ pb: 5 }}>
      <Stack
        direction={{ xs: "column", lg: "row" }}
        justifyContent="space-between"
        alignItems={{ lg: "center" }}
        spacing={2}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{ fontWeight: 950, color: BRAND.dark, letterSpacing: -0.8 }}
          >
            Agenda de servicios
          </Typography>
          <Typography sx={{ color: "rgba(25,36,47,.62)", mt: 0.5 }}>
            Disponibilidad, bloqueos y turnos cargados manualmente.
          </Typography>
        </Box>
        <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
          <Tooltip title="Actualizar agenda">
            <span>
              <IconButton
                onClick={loadAgenda}
                disabled={loading || !selectedServiceId}
                sx={{ border: "1px solid rgba(25,36,47,.12)", borderRadius: 2.5 }}
              >
                <RefreshRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<BlockRoundedIcon />}
            disabled={!selectedServiceId}
            onClick={() => setBlockDialog(true)}
            sx={{ textTransform: "none", fontWeight: 850, borderRadius: 2.5 }}
          >
            Bloquear horario
          </Button>
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            disabled={!selectedServiceId}
            onClick={() => {
            const mode =
              selectedService?.bookingPaymentMode ||
              selectedService?.pagoReserva ||
              "none";
            setAppointmentForm({
              ...emptyAppointment,
              paymentStatus: mode === "none" ? "not_required" : "pending",
            });
            setAppointmentDialog(true);
          }}
            sx={{
              textTransform: "none",
              fontWeight: 900,
              borderRadius: 2.5,
              bgcolor: BRAND.orange,
              "&:hover": { bgcolor: "#DB4B2F" },
            }}
          >
            Cargar turno
          </Button>
        </Stack>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {success ? <Alert severity="success">{success}</Alert> : null}

      <Paper
        elevation={0}
        sx={{ p: 2.5, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)" }}
      >
        <TextField
          select
          label="Servicio a administrar"
          value={selectedServiceId}
          onChange={(event) => setSelectedServiceId(event.target.value)}
          fullWidth
          helperText="La agenda se configura por servicio. Los bloqueos también pueden afectar toda la agenda."
        >
          {services.map((service) => (
            <MenuItem key={itemId(service)} value={itemId(service)}>
              {itemName(service)} · {durationLabel(service)}
              {!requiresBooking(service) ? " · Turnos desactivados" : ""}
            </MenuItem>
          ))}
        </TextField>
        {!services.length ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            Primero creá una publicación de tipo servicio y activá “Requiere reserva o turno”.
          </Alert>
        ) : selectedService && !requiresBooking(selectedService) ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            Este servicio todavía no tiene habilitada la reserva. Podés preparar su agenda, pero no debería publicarse hasta activar esa opción en Productos y servicios.
          </Alert>
        ) : null}
      </Paper>

      {loading ? <LinearProgress /> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2,1fr)", lg: "repeat(4,1fr)" },
          gap: 2,
        }}
      >
        {[
          ["Próximos turnos", summary.upcoming],
          ["Por confirmar", summary.pending],
          ["Pagados", summary.paid],
          ["Bloqueos", summary.blocks],
        ].map(([label, value]) => (
          <Paper
            key={label}
            elevation={0}
            sx={{ p: 2.25, borderRadius: 3, border: "1px solid rgba(25,36,47,.08)" }}
          >
            <Typography variant="body2" sx={{ color: "rgba(25,36,47,.58)", fontWeight: 800 }}>
              {label}
            </Typography>
            <Typography variant="h4" sx={{ mt: 0.4, color: BRAND.dark, fontWeight: 950 }}>
              {value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(25,36,47,.08)", overflow: "hidden" }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "center" }}
          spacing={1}
          sx={{ p: 2.5 }}
        >
          <Box>
            <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
              Disponibilidad semanal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Primera franja diaria. Los bloqueos sirven para excepciones, feriados o ausencias.
            </Typography>
          </Box>
          <Button
            startIcon={<SaveRoundedIcon />}
            variant="contained"
            disabled={!selectedServiceId || savingSchedule}
            onClick={saveSchedule}
            sx={{ textTransform: "none", fontWeight: 900, borderRadius: 2.5 }}
          >
            {savingSchedule ? "Guardando…" : "Guardar horarios"}
          </Button>
        </Stack>
        <Divider />
        <Stack spacing={0}>
          {schedule.map((day) => (
            <Stack
              key={day.weekday}
              direction={{ xs: "column", sm: "row" }}
              alignItems={{ sm: "center" }}
              spacing={2}
              sx={{ px: 2.5, py: 1.5, borderBottom: "1px solid rgba(25,36,47,.07)" }}
            >
              <Stack direction="row" alignItems="center" spacing={1} sx={{ minWidth: 170 }}>
                <Switch
                  checked={day.enabled}
                  onChange={(event) =>
                    updateScheduleDay(day.weekday, { enabled: event.target.checked })
                  }
                />
                <Typography sx={{ color: BRAND.dark, fontWeight: 850 }}>{day.label}</Typography>
              </Stack>
              <TextField
                label="Desde"
                type="time"
                value={day.startTime}
                disabled={!day.enabled}
                onChange={(event) =>
                  updateScheduleDay(day.weekday, { startTime: event.target.value })
                }
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <TextField
                label="Hasta"
                type="time"
                value={day.endTime}
                disabled={!day.enabled}
                onChange={(event) =>
                  updateScheduleDay(day.weekday, { endTime: event.target.value })
                }
                InputLabelProps={{ shrink: true }}
                size="small"
              />
              <Typography variant="body2" color="text.secondary">
                {day.enabled ? "Disponible" : "No se ofrecen turnos"}
              </Typography>
            </Stack>
          ))}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(25,36,47,.08)", overflow: "hidden" }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ p: 2.5 }}>
          <EventAvailableRoundedIcon sx={{ color: BRAND.orange }} />
          <Box>
            <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
              Próximos turnos
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Ventana de los próximos 60 días.
            </Typography>
          </Box>
        </Stack>
        <Divider />
        <TableContainer>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow>
                {["Fecha", "Cliente", "Servicio", "Estado", "Pago", "Notas"].map((label) => (
                  <TableCell key={label} sx={{ fontWeight: 950, color: "rgba(25,36,47,.58)" }}>
                    {label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {!appointments.length ? (
                <TableRow>
                  <TableCell colSpan={6} sx={{ py: 7, textAlign: "center" }}>
                    <CalendarMonthRoundedIcon sx={{ fontSize: 38, color: "rgba(25,36,47,.25)" }} />
                    <Typography sx={{ mt: 1, color: BRAND.dark, fontWeight: 900 }}>
                      No hay turnos en esta ventana.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : null}
              {appointments.map((appointment) => (
                <TableRow key={appointment.id || appointment._id} hover>
                  <TableCell>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 850 }}>
                      {formatDateTime(appointment.startAt)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {appointment.appointmentNumber}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ color: BRAND.dark, fontWeight: 800 }}>
                      {appointment.customer?.fullName || "—"}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {appointment.customer?.phone || ""}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {appointment.serviceSnapshot?.name || itemName(selectedService)}
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={appointment.status || "pending"}
                      onChange={(event) =>
                        updateAppointment(appointment.id || appointment._id, {
                          status: event.target.value,
                        })
                      }
                      sx={{ minWidth: 145 }}
                    >
                      {["pending", "confirmed", "completed", "no_show", "cancelled"].map((status) => (
                        <MenuItem key={status} value={status}>
                          {statusLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell>
                    <TextField
                      select
                      size="small"
                      value={appointment.payment?.status || "not_required"}
                      onChange={(event) =>
                        updateAppointment(appointment.id || appointment._id, {
                          paymentStatus: event.target.value,
                        })
                      }
                      sx={{ minWidth: 145 }}
                    >
                      {[
                        "not_required",
                        "pending",
                        "in_process",
                        "approved",
                        "rejected",
                        "cancelled",
                        "refunded",
                      ].map((status) => (
                        <MenuItem key={status} value={status}>
                          {paymentLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 280 }}>
                    <Typography variant="body2" noWrap title={appointment.notes || ""}>
                      {appointment.notes || "—"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper
        elevation={0}
        sx={{ borderRadius: 3, border: "1px solid rgba(25,36,47,.08)", overflow: "hidden" }}
      >
        <Box sx={{ p: 2.5 }}>
          <Typography variant="h6" sx={{ color: BRAND.dark, fontWeight: 950 }}>
            Bloqueos vigentes
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Horarios ocupados manualmente, ausencias o cierres excepcionales.
          </Typography>
        </Box>
        <Divider />
        <TableContainer>
          <Table sx={{ minWidth: 720 }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 950 }}>Desde</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Hasta</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Alcance</TableCell>
                <TableCell sx={{ fontWeight: 950 }}>Motivo</TableCell>
                <TableCell align="right" sx={{ fontWeight: 950 }}>Acción</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!blocks.length ? (
                <TableRow>
                  <TableCell colSpan={5} sx={{ py: 4, textAlign: "center", color: "text.secondary" }}>
                    No hay bloqueos vigentes.
                  </TableCell>
                </TableRow>
              ) : null}
              {blocks.map((block) => (
                <TableRow key={block.id || block._id}>
                  <TableCell>{formatDateTime(block.startAt)}</TableCell>
                  <TableCell>{formatDateTime(block.endAt)}</TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={block.serviceId ? "Este servicio" : "Toda la agenda"}
                    />
                  </TableCell>
                  <TableCell>{block.reason || "Sin motivo"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Quitar bloqueo">
                      <IconButton onClick={() => removeBlock(block)} sx={{ color: BRAND.orange }}>
                        <DeleteRoundedIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={appointmentDialog} onClose={() => setAppointmentDialog(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={createAppointment}>
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Cargar turno manual</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <Alert severity="info">
                {selectedService ? `${itemName(selectedService)} · ${durationLabel(selectedService)}` : "Servicio"}
              </Alert>
              <TextField
                label="Nombre del cliente"
                required
                value={appointmentForm.fullName}
                onChange={(event) => setAppointmentForm((current) => ({ ...current, fullName: event.target.value }))}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  label="Teléfono"
                  required
                  value={appointmentForm.phone}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, phone: event.target.value }))}
                  fullWidth
                />
                <TextField
                  label="Email"
                  type="email"
                  value={appointmentForm.email}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, email: event.target.value }))}
                  fullWidth
                />
              </Stack>
              <TextField
                label="Fecha y hora"
                type="datetime-local"
                required
                value={appointmentForm.startAt}
                onChange={(event) => setAppointmentForm((current) => ({ ...current, startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <TextField
                  select
                  label="Estado"
                  value={appointmentForm.status}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, status: event.target.value }))}
                  fullWidth
                >
                  <MenuItem value="confirmed">Confirmado</MenuItem>
                  <MenuItem value="pending">Pendiente</MenuItem>
                </TextField>
                <TextField
                  select
                  label="Estado del pago"
                  value={appointmentForm.paymentStatus}
                  onChange={(event) => setAppointmentForm((current) => ({ ...current, paymentStatus: event.target.value }))}
                  fullWidth
                >
                  <MenuItem value="not_required">No requerido</MenuItem>
                  <MenuItem value="pending">Pendiente</MenuItem>
                  <MenuItem value="approved">Pagado manualmente</MenuItem>
                </TextField>
              </Stack>
              <TextField
                label="Notas internas"
                multiline
                minRows={3}
                value={appointmentForm.notes}
                onChange={(event) => setAppointmentForm((current) => ({ ...current, notes: event.target.value }))}
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setAppointmentDialog(false)} disabled={savingAppointment}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={savingAppointment}>
              {savingAppointment ? "Guardando…" : "Guardar turno"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog open={blockDialog} onClose={() => setBlockDialog(false)} fullWidth maxWidth="sm">
        <Box component="form" onSubmit={createBlock}>
          <DialogTitle sx={{ fontWeight: 950, color: BRAND.dark }}>Bloquear horario</DialogTitle>
          <DialogContent dividers>
            <Stack spacing={2} sx={{ mt: 0.5 }}>
              <TextField
                select
                label="Aplicar a"
                value={blockForm.scope}
                onChange={(event) => setBlockForm((current) => ({ ...current, scope: event.target.value }))}
                fullWidth
              >
                <MenuItem value="service">Solo {itemName(selectedService)}</MenuItem>
                <MenuItem value="all">Toda la agenda</MenuItem>
              </TextField>
              <TextField
                label="Desde"
                type="datetime-local"
                required
                value={blockForm.startAt}
                onChange={(event) => setBlockForm((current) => ({ ...current, startAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Hasta"
                type="datetime-local"
                required
                value={blockForm.endAt}
                onChange={(event) => setBlockForm((current) => ({ ...current, endAt: event.target.value }))}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Motivo"
                value={blockForm.reason}
                onChange={(event) => setBlockForm((current) => ({ ...current, reason: event.target.value }))}
                placeholder="Turno telefónico, feriado, ausencia, capacitación..."
                fullWidth
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setBlockDialog(false)} disabled={savingBlock}>Cancelar</Button>
            <Button type="submit" variant="contained" disabled={savingBlock}>
              {savingBlock ? "Guardando…" : "Bloquear"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>
    </Stack>
  );
}
